// The asset drawer `?asset=<id>` (spec/dashboard.md section 9.7): the record fields (staged into the asset's draft;
// Cancel reverts the drawer's edits, section 4.4), the preview, the content URL with Copy, Replace bytes… (a picker, a
// confirmation naming the references, then `PUT …/assets/{asset}/content` — the id and every reference stay), Where used.
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Asset, Json } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { EmptyState } from '../../ui/Banner';
import { Field } from '../../ui/Field';
import { AlertCircle, Copy, Images, Upload } from '../../ui/icons';
import { CommitTextInput, NumberInput, Select, TextArea } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';
import { ASSET_KINDS, AssetPreview, copyText, errorText, formatBytes, GroupSelect, ReferenceList, stageAsset, useAssetReferenceLoader, type AssetRef } from './common';

export function AssetDrawer({ server, loading, groups, canWrite, onClose, onDelete, onRestore }: { server: Asset | undefined; loading: boolean; groups: string[]; canWrite: boolean; onClose: () => void; onDelete: (a: Asset) => void; onRestore: (a: Asset) => void }) {
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const [mark] = useState(() => workspace.mark());
  const cancel = () => {
    workspace.revertTo(mark);
    onClose();
  };
  return (
    <Modal
      drawer
      open
      title={server ? server.name : loading ? 'Loading…' : 'Asset not found'}
      onClose={onClose}
      footer={
        server ? (
          <>
            {server.deletedAt ? (
              <Button onClick={() => onRestore(server)} missingPermission={canWrite ? null : 'content_write'}>
                Restore
              </Button>
            ) : (
              <Button variant="danger" onClick={() => onDelete(server)} missingPermission={canWrite ? null : 'content_write'}>
                Delete
              </Button>
            )}
            <span className="spacer" />
            <Button onClick={cancel}>Cancel</Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : undefined
      }
    >
      <div data-testid="asset-drawer">
        {server ? (
          <DrawerBody server={server} groups={groups} canWrite={canWrite} />
        ) : loading ? (
          <div className="skeleton-rows" role="status" aria-label="Loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={<Images />} title="No asset with this id on this branch" body="It may have been deleted for good, or it lives on another branch." />
        )}
      </div>
    </Modal>
  );
}

function DrawerBody({ server, groups, canWrite }: { server: Asset; groups: string[]; canWrite: boolean }) {
  const { org, game, offsets } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  const confirm = useConfirm();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const loadRefs = useAssetReferenceLoader();
  const draft = workspace.getDraft(entityKey('asset', server.id));
  const asset = ((draft?.current as unknown as Asset | undefined) ?? server) as Asset;
  const deleted = !!server.deletedAt;
  const editable = canWrite && !deleted;
  const stage = (field: Parameters<typeof stageAsset>[2], value: unknown) => stageAsset(workspace, server, field, value);

  const [refs, setRefs] = useState<AssetRef[] | null>(null);
  const [refsError, setRefsError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let cancelled = false;
    setRefs(null);
    setRefsError(null);
    loadRefs(server.id).then(
      (r) => !cancelled && setRefs(r),
      (e: unknown) => !cancelled && setRefsError(errorText(e)),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server.id, server.version, workspace.head]);

  const replace = async (file: File) => {
    if (draft) {
      toasts.error('Save or discard the record edits of this asset first: replacing the bytes is a commit that moves its version.');
      return;
    }
    let current: AssetRef[];
    try {
      current = await loadRefs(server.id);
    } catch (e) {
      toasts.error(errorText(e));
      return;
    }
    const n = current.length;
    const ok = await confirm({
      title: `Replace the bytes of ${server.name}?`,
      body: (
        <>
          <p>
            {file.name} ({formatBytes(file.size)}) replaces the content. The id and {n === 0 ? 'its references' : `the ${n} reference${n === 1 ? '' : 's'}`} stay; {n === 0 ? 'no document references this asset.' : `${n === 1 ? 'the document' : 'every document'} below gets the new bytes with the next release.`}
          </p>
          {n > 0 && <ReferenceList refs={current} />}
        </>
      ),
      confirmLabel: 'Replace bytes',
      tone: 'danger',
    });
    if (!ok) return;
    setReplacing(true);
    try {
      const result = await client.call(ops.putAssetContent, { path: { org, game: game.slug, branch, asset: server.id }, rawBody: file, contentType: file.type || 'application/octet-stream', ifMatch: Number(server.version) });
      const updated = result.data as unknown as Asset;
      toasts.success(updated?.sha256 && updated.sha256 === server.sha256 ? `Unchanged bytes: ${file.name} is identical to the current content.` : `The bytes of ${server.name} were replaced.`);
      catalog.refresh();
    } catch (e) {
      toasts.error(errorText(e));
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div className="asset-drawer-body">
      <div className="asset-hero">
        <div className="asset-hero-preview">
          <AssetPreview asset={asset} large />
        </div>
        <div className="asset-hero-facts">
          <div className="facts">
            <Badge>{asset.kind}</Badge>
            {draft && <Badge tone="warning">MOD</Badge>}
            {deleted && <Badge tone="danger">Deleted</Badge>}
          </div>
          <dl className="kv asset-facts">
            <dt>Size</dt>
            <dd>
              {formatBytes(server.bytes)} · {server.contentType ?? 'no content type'}
              {server.extension ? ` · .${server.extension}` : ''}
            </dd>
            <dt>Id</dt>
            <dd>
              <code>{server.id}</code>
            </dd>
            {server.unnyId && (
              <>
                <dt>Legacy id</dt>
                <dd>
                  <code>{server.unnyId}</code>
                </dd>
              </>
            )}
            <dt>Version</dt>
            <dd className="num">v{String(server.version)}</dd>
            {server.sha256 && (
              <>
                <dt>sha256</dt>
                <dd>
                  <code title={server.sha256}>{server.sha256.slice(0, 12)}</code>
                </dd>
              </>
            )}
            {deleted && (
              <>
                <dt>Deleted</dt>
                <dd className="asset-deleted-at">{formatInstantWithZone(server.deletedAt, zone, { offsets })}</dd>
              </>
            )}
          </dl>
        </div>
      </div>

      <section className="asset-drawer-section">
        <div className="section-head">
          <h3>Record</h3>
          <span className="muted small">Edits are staged; Save All commits them.</span>
        </div>
        <div className="form-grid">
          <Field label="Name" required>
            <CommitTextInput value={asset.name} disabled={!editable} onCommit={(v) => stage('name', v)} />
          </Field>
          <Field label="Kind">
            <Select value={asset.kind} disabled={!editable} onChange={(e) => stage('kind', e.target.value)}>
              {(ASSET_KINDS as readonly string[]).includes(asset.kind) ? null : <option value={asset.kind}>{asset.kind}</option>}
              {ASSET_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Group">
            <GroupSelect value={asset.group} groups={groups} disabled={!editable} label="Group" onChange={(g) => stage('group', g)} />
          </Field>
          <Field label="Flags" help="The record's flag bits (an integer).">
            <NumberInput value={Number(asset.flags ?? 0)} disabled={!editable} onCommit={(v) => stage('flags', v ?? 0)} />
          </Field>
          <Field className="span-2" label="Description">
            <CommitTextInput multiline value={asset.description ?? ''} disabled={!editable} onCommit={(v) => stage('description', v === '' ? null : v)} />
          </Field>
          <div className="span-2">
            <MetaEditor value={asset.meta as unknown} disabled={!editable} onCommit={(v) => stage('meta', v)} />
          </div>
        </div>
      </section>

      <section className="asset-drawer-section">
        <div className="section-head">
          <h3>Content</h3>
        </div>
        <Field label="Content URL" help="The public release path of the bytes (by content hash).">
          {server.contentPath ? (
            <span className="asset-url-block">
              <span className="asset-url-row">
                <code className="asset-url">{server.contentPath}</code>
                <Button size="s" icon={<Copy aria-hidden="true" />} aria-label="Copy content URL" onClick={() => copyText(server.contentPath!, () => toasts.info('Copied.'))}>
                  Copy
                </Button>
              </span>
              <span className="muted small" data-testid="content-url-note">
                This URL works once a release that lists the asset is pinned; until then it answers 404 (the previews here use short-lived signed URLs).
              </span>
            </span>
          ) : (
            <span className="muted">No content uploaded yet.</span>
          )}
        </Field>
        <div className="asset-replace">
          <input
            ref={fileInput}
            type="file"
            hidden
            aria-label="Replacement file"
            disabled={!editable || replacing}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void replace(f);
            }}
          />
          <Button onClick={() => fileInput.current?.click()} busy={replacing} disabled={deleted} missingPermission={canWrite ? null : 'content_write'} title={deleted ? 'Restore the asset first.' : undefined} icon={<Upload aria-hidden="true" />}>
            Replace bytes…
          </Button>
          <span className="muted small">The id and every reference stay.</span>
        </div>
      </section>

      <section className="asset-drawer-section">
        <div className="section-head">
          <h3 className="asset-section-title">Where used</h3>
          {refs && <span className="chip">{refs.length}</span>}
        </div>
        {refsError ? (
          <div className="asset-alert" role="alert">
            <AlertCircle className="asset-alert-icon" aria-hidden="true" />
            <span>{refsError}</span>
          </div>
        ) : refs === null ? (
          <div className="skeleton-rows" role="status" aria-label="Loading">
            {[0, 1].map((i) => (
              <div key={i} className="skeleton-row">
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ))}
          </div>
        ) : (
          <ReferenceList refs={refs} testId="asset-drawer-references" />
        )}
      </section>
    </div>
  );
}

/** `meta` as JSON: committed on blur when it parses (free-form editor data: pixels per unit, 9-slice, dimensions). */
function MetaEditor({ value, disabled, onCommit }: { value: unknown; disabled: boolean; onCommit: (v: Json) => void }) {
  const toText = (v: unknown) => (v === null || v === undefined ? '' : JSON.stringify(v, null, 2));
  const [text, setText] = useState(toText(value));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(toText(value));
    setError(null);
  }, [value]);
  const commit = () => {
    const t = text.trim();
    let parsed: Json;
    try {
      parsed = t === '' ? null : (JSON.parse(t) as Json);
    } catch {
      setError('Not valid JSON: the change is not staged.');
      return;
    }
    setError(null);
    if (JSON.stringify(parsed) !== JSON.stringify(value ?? null)) onCommit(parsed);
  };
  return (
    <Field label="Meta" help="Free-form JSON for the editor (dimensions, pixels per unit, 9-slice)." error={error}>
      <TextArea className="asset-meta" rows={4} value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} onBlur={commit} spellCheck={false} />
    </Field>
  );
}
